<template>
    <b-card no-body class="shadow-sm mt-4">
        <b-card-header class="bg-primary text-white py-3 d-flex justify-content-between align-items-center">
            <h1 class="mb-0">
                Compatibilidad de Productos
                <template>
                    <b-badge class="ml-2 counter-pending" v-if="pendingCountComputed > 0">
                        {{ pendingCountComputed }} Por verificar
                    </b-badge>
                </template>
            </h1>
            <b-button variant="light" size="sm" @click="toggleTableView" class="ml-2">
                {{ showFullTable ? 'Mostrar solo encabezado' : 'Mostrar tabla completa' }}
            </b-button>
        </b-card-header>

        <b-card-body class="p-0">
            <div class="table-responsive">
                <b-table-simple hover class="mb-0">
                    <thead>
                        <tr>
                            <th class="product-cell border-right text-center align-middle">
                                <div class="product-header-title">Producto</div>
                            </th>
                            <template v-for="product in productsSorted">
                                <th class="product-header border-right">
                                    <b-card no-body class="product-card bg-light">
                                        <b-card-body class="p-2">
                                            <h6 class="product-name mb-1">{{ product.name }}</h6>                                            
                                            <div class="product-type small">{{ product.brand }}</div>
                                            <div class="product-custom-field small" v-if="selectedCustomField && customFieldValues[product.id]">
                                                {{ customFieldValues[product.id] }}
                                            </div>
                                            <b-badge variant="secondary">{{ product.state }}</b-badge>
                                            <div class="ph-input-group my-1">
                                                <label class="ph-label mb-0">pH:</label>
                                                <div class="d-flex align-items-center">
                                                    <b-form-input v-model="product.ph" type="number"
                                                        :placeholder="product.ph === null ? '-' : ''"
                                                        class="ph-input mr-1"></b-form-input>
                                                    <b-button size="sm" variant="primary" class="save-ph-btn"
                                                        title="Guardar pH" @click="savePh(product)">
                                                        <i class="fa fa-floppy-o fa-lg"></i>
                                                    </b-button>
                                                </div>
                                            </div>
                                            <div class="pictograms">
                                                <span v-for="pictogram in product.pictograms" :key="pictogram">
                                                    <img :src="pictogram" alt="Pictogram" class="pictogram">
                                                </span>
                                            </div>
                                        </b-card-body>
                                    </b-card>
                                </th>
                            </template>
                        </tr>
                    </thead>
                    <tbody :class="{ 'd-none': !showFullTable }">
                        <tr v-for="product1 in productsSorted" :key="product1.id">
                            <td class="product-cell border-right">
                                <b-card no-body class="product-card bg-light">
                                    <b-card-body class="p-2">
                                        <h6 class="product-name mb-1">{{ product1.name }}</h6>                                        
                                        <div class="product-type small">{{ product1.brand }}</div>
                                        <div class="product-custom-field small" v-if="selectedCustomField && customFieldValues[product1.id]">
                                            {{ customFieldValues[product1.id] }}
                                        </div>
                                        <b-badge variant="secondary">{{ product1.state }}</b-badge>
                                        <div class="product-ph my-1">
                                            <b-badge variant="info" class="text-white">pH: {{ product1.ph || '-'
                                                }}</b-badge>
                                        </div>
                                        <div class="pictograms">
                                            <span v-for="pictogram in product1.pictograms" :key="pictogram">
                                                <img :src="pictogram" alt="Pictogram" class="pictogram">
                                            </span>
                                        </div>
                                    </b-card-body>
                                </b-card>
                            </td>
                            <template v-for="product2 in productsSorted">
                                <td class="compatibility-cell border-right">
                                    <div :class="['status-card', getStatusClass(product1, product2)]">
                                        <div class="status-text">{{ getStatusText(product1, product2) }}</div>
                                        <template
                                            v-if="getCompatibilityStatus(comparacion, product1, product2) === 'revision' && verification(comparacion, product1, product2)">
                                            <div class="mt-1">
                                                <b-button variant="primary" @click="compatibility(product1, product2)"
                                                    title="Verificar">
                                                    Verificar
                                                </b-button>
                                            </div>
                                        </template>
                                    </div>
                                </td>
                            </template>
                        </tr>
                    </tbody>
                </b-table-simple>
            </div>
        </b-card-body>

        <b-modal v-model="modal" title="Revisión de Compatibilidad" hide-footer centered size="xl">
            <comparison :products="productsCompatibility" />
            <div class="compatibility-actions" v-if="canCreateComparisons">
                <b-button class="compatibility-btn status-compatible" @click="markAsCompatible(product1, product2)">
                    <span class="icon">✓</span>
                    <span class="text">Marcar como compatibles</span>
                </b-button>

                <b-button class="compatibility-btn status-incompatible" @click="markAsIncompatible(product1, product2)">
                    <span class="icon">✕</span>
                    <span class="text">Marcar como incompatibles</span>
                </b-button>
            </div>
        </b-modal>

    </b-card>
</template>

<script>

// Vuex
import { mapState, mapActions } from 'vuex'
import comparison from '../approveMatrix/Comparison';

// Mixins 
import matrixCompatibilityIndex from '@/servicios/matrizCompatibilidad/compatibility/index.js'

export default {
    name: 'Compatibility',
    components: {
        comparison
    },

    mixins: [matrixCompatibilityIndex],

    props: {
        comparacion: {
            type: Object,
            required: true
        },
        pendingCount: {
            type: Number,
            default: null
        },
        customFieldValues: {
            type: Object,
            default: null
        },
        selectedCustomField: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            showFullTable: true,
            modal: false,
            productsCompatibility: null,
            product1: null,
            product2: null,
        }
    },
    computed: {

        ...mapState('compatibilityMatrixComparisonIA', [
            'scheduledProcesses'
        ]),

        // Verificar que no hayan procesos agendados con IA (Gemini) para comparaciones 
        canCreateComparisons() {
            if (!this.scheduledProcesses) {
                return true
            }

            if (this.scheduledProcesses.status === 'pending' || this.scheduledProcesses.status === 'processing') {
                return false
            }

            return true
        },

        productsSorted() {
            if (!this.comparacion || !this.comparacion.matriz_compatibilidad_productos || 
                !Array.isArray(this.comparacion.matriz_compatibilidad_productos)) {
                return [];
            }
            
            return this.comparacion.matriz_compatibilidad_productos
            .map(element => {
                if (!element || !element.producto) {
                return {};
                }
                return {
                id: element.producto.id,
                name: element.producto.nombre,
                brand: element.producto.fabricante ? element.producto.fabricante.nombre : '',
                pictograms: element.producto.pictogramas ? element.producto.pictogramas.map(pictograma => pictograma.direccion) : [],
                state: element.producto.lista_estado ? element.producto.lista_estado.lista : null,
                ph: element.producto.propiedades ? element.producto.propiedades.ph : null,
                attachments: process.env.VUE_APP_API_URL + 'storage/files/' +
                    (Array.isArray(element.producto.adjuntos) && element.producto.adjuntos.length > 0
                    ? element.producto.adjuntos[0].adjunto
                    : '')
                };
            })
            .filter(product => product.id && product.name)
            .sort((a, b) => {
                if (!a.name) return 1;
                if (!b.name) return -1;
                return a.name.localeCompare(b.name);
            });
        },
        pendingCountComputed() {
            if (this.pendingCount !== null && this.pendingCount !== undefined) {
                return this.pendingCount;
            }
            if (this.comparacion && (this.comparacion.pending_verifications_count !== null && this.comparacion.pending_verifications_count !== undefined)) {
                return this.comparacion.pending_verifications_count;
            }
            return null;
        }
    },
    methods: {
        ...mapActions('propiedades', ['savePhProperties']),
        ...mapActions('matrizCompatibilidadAlmacenamiento', [
            'saveHistoryCompatibility',
        ]),
        toggleTableView() {
            this.showFullTable = !this.showFullTable
        },
        
        getStatusClass(product1, product2) {
            const status = this.getCompatibilityStatus(this.comparacion, product1, product2);
            return {
                'status-compatible': status === 'compatible',
                'status-incompatible': status === 'incompatible',
                'status-revision': status === 'revision'
            }
        },
        getStatusText(product1, product2) {
            const status = this.getCompatibilityStatus(this.comparacion, product1, product2);
            return status === 'compatible' ? 'Compatible' :
                status === 'incompatible' ? 'Incompatible' :
                    'Por Verificar';
        },
        async updateCompatibility(product1, product2, status) {
            this.$alertConfirmation({
                icon: "warning",
                title: "Solicitud para validar compatibilidad",
                text: `Está a punto de solicitar la auditoría en la compatibilidad entre el producto "${product1.name}" con "${product2.name}" correspondiente a "${status}". ¿Está seguro de continuar?`,
                showConfirmButton: true,
                showCancelButton: true,
                confirmButtonText: "Actualizar",
                cancelButtonText: "Cancelar",
                onConfirm: async () => {
                    const history = {
                        first_prod_product_id: product1.id,
                        second_prod_product_id: product2.id,
                        result: status === 'Compatible' ? 1 : -1
                    }
                    const res = await this.saveHistoryCompatibility(history);
                    if (res) {
                        this.modal = false;
                        this.$alertSuccess(`Se ha realizado la solicitud de auditoría en la compatibilidad entre los productos ${product1.name} y ${product2.name}. Una vez aprobadas todas las compatibilidades, se podrá descargar la matriz`, 10000, true, '¡Solicitud completada!');
                        this.$emit('handleGetComparacion');
                    }
                }
            });
        },
        markAsCompatible(product1, product2) {
            this.updateCompatibility(product1, product2, "Compatible");
        },
        markAsIncompatible(product1, product2) {
            this.updateCompatibility(product1, product2, "Incompatible");
        },
        async savePh(product) {
            if (product.ph) {
                if (product.ph < 0 || product.ph > 14) {
                    this.$alertError('El valor del pH debe estar entre 0 y 14');
                    return;
                }
                const res = await this.savePhProperties({
                    id_producto: product.id,
                    ph: product.ph
                });
                if (res) {
                    this.$alertSuccess('El pH del producto se ha guardado correctamente');
                    this.$emit('handleUpdateComparacion');
                }
            }
        },
        compatibility(product1, product2) {
            this.product1 = product1;
            this.product2 = product2;
            this.productsCompatibility = [product1, product2];
            this.modal = true;
        },
        
    }
}
</script>

<style scoped>
body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    background-color: #f8f9fa;
    color: #212529;
}

.product-header-title {
    font-weight: 600;
    font-size: 1rem;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.product-card {
    border: none !important;
    height: 100%;
    padding: 0 !important;
    margin: 0 !important;
    min-height: 200px;
}

.product-header {
    padding: 0 !important;
    margin: 0 !important;
}

.product-cell {
    padding: 2px !important;
    margin: 0 !important;
}

.status-card {
    height: 100%;
    width: 100%;
    min-height: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
    padding: 0.5rem;
}

.status-text {
    font-weight: 500;
    font-size: 0.85rem;
    margin-bottom: 0.25rem;
}

.status-compatible {
    background-color: #d4edda;
    color: #155724;
}

.status-incompatible {
    background-color: #f8d7da;
    color: #721c24;
}

.status-revision {
    background-color: #fff3cd;
    color: #856404;
}

.status-compatible:hover {
    background-color: #c3e6cb;
    border-color: #b1dfbb;
}

.status-incompatible:hover {
    background-color: #f5c6cb;
    border-color: #f1b0b7;
}

.status-revision:hover {
    background-color: #ffeeba;
    border-color: #ffdf7e;
}

.compatibility-cell {
    padding: 0 !important;
    vertical-align: middle !important;
}

.product-cell,
.product-header,
.compatibility-cell {
    min-width: 180px;
    padding: 0.25rem !important;
    vertical-align: middle !important;
}

.product-name {
    font-size: 0.75rem;
    font-weight: 600;
    color: #2c3e50;
    line-height: 1.2;
}

.product-type {
    color: #6c757d;
    font-size: 0.75rem;
}

.product-custom-field {
    color: #6c757d;
    font-size: 0.75rem;
}

.save-ph-btn {
    padding: 0 0.35rem;
    font-size: 0.7rem;
    line-height: 1.9;
    min-width: 24px;
}

h1 {
    font-size: 1.5rem;
    font-weight: 600;
}

.table-responsive {
    border-radius: 0 0 0.25rem 0.25rem;
    overflow: auto;
}

.pictograms {
    display: flex;
    flex-wrap: wrap;
    gap: 0.15rem;
}

.pictogram {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    background-color: white;
    font-size: 0.85rem;
}

.ph-input-group {
    display: flex;
    flex-direction: column;
}

.ph-label {
    font-size: 0.75rem;
    color: #495057;
}

.ph-input {
    width: 90px;
    height: 24px;
    padding: 0.1rem 0.25rem;
    text-align: center;
    font-size: 0.75rem;
}

.compatibility-actions {
    display: flex;
    justify-content: center;
    gap: 20px;
    margin-top: 20px;
    padding: 15px 0;
    border-top: 1px solid #eee;
    width: 100%;
}

.compatibility-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 20px;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.2s ease;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    border: 1px solid transparent;
    cursor: pointer;
    flex: 0 1 auto;
    max-width: 100%;
}

.compatibility-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.compatibility-btn:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.compatibility-btn .icon {
    font-size: 1.2rem;
    margin-right: 10px;
    flex-shrink: 0;
}

.compatibility-btn .text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.table-responsive {
    max-height: calc(100vh - 150px);
    overflow: auto;
    position: relative;
    background: white;
}

thead th {
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;
}

tbody td:first-child,
thead th:first-child {
    position: sticky;
    left: 0;
    background: white;
    z-index: 20;
}

thead th:first-child {
    z-index: 30;
}

.table-responsive::-webkit-scrollbar {
    height: 8px;
}

.table-responsive::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.table-responsive::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
    background: #555;
}

.table-responsive::-webkit-scrollbar:vertical {
    width: 8px;
}

@media (max-width: 991px) {

    .product-cell,
    .product-header,
    .compatibility-cell {
        min-width: 160px;
    }
}

@media (max-width: 767px) {

    .product-cell,
    .product-header,
    .compatibility-cell {
        min-width: 140px;
    }

    .product-name {
        font-size: 0.75rem;
    }

    .status-text {
        font-size: 0.7rem;
    }

    .pictogram {
        width: 1.5rem;
        height: 1.5rem;
        font-size: 0.75rem;
    }

    .compatibility-actions {
        flex-direction: row;
        flex-wrap: wrap;
        gap: 10px;
        justify-content: center;
        padding: 15px 10px;
    }

    .compatibility-btn {
        flex: 0 1 auto;
        min-width: 0;
    }
}

@media (max-width: 575px) {

    .product-cell,
    .product-header,
    .compatibility-cell {
        min-width: 155px;
    }

    .ph-input-group {
        margin: 0.25rem 0;
    }

    .ph-input {
        width: 60px;
        height: 22px;
    }

    .save-ph-btn {
        height: 22px;
        padding: 0 0.25rem;
    }

    .compatibility-actions {
        gap: 8px;
    }
}

.counter-pending{
    color: #856404;
    background-color: #ffeeba;

}
</style>
